import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton, Chip,
  CircularProgress, Divider, Avatar, Autocomplete,
} from '@mui/material';
import CloseIcon       from '@mui/icons-material/Close';
import SendIcon        from '@mui/icons-material/Send';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PresentToAllIcon from '@mui/icons-material/PresentToAll';
import { QRCodeSVG }   from 'qrcode.react';
import { collectionGroup, getDocs, query, where, addDoc, collection } from 'firebase/firestore';
import { db }          from '../../firebase/config';
import { callClaude }  from '../../utils/claudeProxy';
import { authFetch } from '../../utils/authFetch';

// ── AI summary generation ─────────────────────────────────────────────────────

async function generateSummary({ pitch }) {
  const slideTypes = (pitch.slides || [])
    .filter(s => !s.hidden)
    .map(s => s.type)
    .join(', ');
  const prompt = `Skriv en kort och säljande sammanfattning (2–3 meningar, svenska) av presentationen nedan.
Tonen ska vara professionell men entusiastisk. Sammanfattningen ska motivera mottagaren att öppna presentationen.

Presentation: "${pitch.title || 'Namnlös pitch'}"
Kund/projekt: "${pitch.client || ''}"
Antal slides: ${(pitch.slides || []).filter(s => !s.hidden).length}
Slidtyper: ${slideTypes}
${pitch.userPrompt ? `Syfte: ${pitch.userPrompt}` : ''}

Skriv bara sammanfattningstexten direkt, inga rubriker.`;
  try {
    const json = await callClaude({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 300,
      messages: [{ role: 'user', content: prompt }],
    });
    return json?.content?.[0]?.text?.trim() || null;
  } catch {
    return null;
  }
}

// ── Email preview ─────────────────────────────────────────────────────────────

function EmailPreview({ subject, bodyText, heroImageUrl, shareUrl, pitchTitle }) {
  const accent = '#1565C0';
  const lines  = (bodyText || '').split('\n').filter(Boolean);
  return (
    <Box sx={{ fontFamily: "'Segoe UI', Arial, sans-serif", bgcolor: '#f5f5f5', p: 2, borderRadius: 2, height: '100%', overflowY: 'auto' }}>
      <Box sx={{ maxWidth: 480, mx: 'auto', bgcolor: '#fff', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
        {/* Header */}
        <Box sx={{ bgcolor: accent, px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PresentToAllIcon sx={{ color: '#fff', fontSize: 22 }} />
          <Box>
            <Typography fontWeight={900} sx={{ color: '#fff', fontSize: '1.1rem', lineHeight: 1 }}>Presentation</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem' }}>LagbasN · PitchN</Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          <Typography fontWeight={700} sx={{ fontSize: '1rem', borderBottom: `2px solid ${accent}`, pb: 1, mb: 2 }}>
            {subject || '(Ämnesrad visas här)'}
          </Typography>

          {/* Hero image */}
          {heroImageUrl && (
            <Box component="img" src={heroImageUrl} alt=""
              sx={{ width: '100%', borderRadius: 1, mb: 2, display: 'block', maxHeight: 180, objectFit: 'cover' }} />
          )}

          {/* Body */}
          {lines.map((l, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 1.5, lineHeight: 1.7, color: '#222', fontSize: '0.88rem' }}>{l}</Typography>
          ))}
          {!lines.length && (
            <Typography variant="body2" sx={{ color: '#bbb', fontStyle: 'italic' }}>Skriv eller generera en sammanfattning...</Typography>
          )}

          {shareUrl && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ textAlign: 'center', mb: 1.5 }}>
                <Box component="a" href={shareUrl} sx={{
                  display: 'inline-block', bgcolor: accent, color: '#fff',
                  px: 3, py: 1.25, borderRadius: 1.5, textDecoration: 'none',
                  fontWeight: 700, fontSize: '0.85rem',
                }}>
                  Öppna presentation →
                </Box>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <QRCodeSVG value={shareUrl} size={80} level="H" />
                <Typography variant="caption" sx={{ color: '#aaa', display: 'block', mt: 0.5, fontSize: '0.65rem' }}>
                  {shareUrl}
                </Typography>
              </Box>
            </>
          )}

          <Typography sx={{ fontSize: '0.65rem', color: '#ccc', mt: 2.5, textAlign: 'center' }}>
            LagbasN · lagbasn.se
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ── Main dialog ───────────────────────────────────────────────────────────────

export default function PitchMailDialog({ open, onClose, pitch, backgrounds, currentUser }) {
  const shareUrl    = pitch ? `${window.location.origin}/p/${pitch.shareToken}` : '';
  const defaultSubject = pitch ? `Presentation: ${pitch.title || 'Namnlös pitch'}` : '';

  const [subject,     setSubject]     = useState('');
  const [bodyText,    setBodyText]    = useState('');
  const [recipients,  setRecipients]  = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [generating,  setGenerating]  = useState(false);
  const [sending,     setSending]     = useState(false);
  const contactsLoaded = useRef(false);

  // Hero image: background URL of the first non-hidden slide
  const heroImageUrl = (() => {
    if (!pitch || !backgrounds?.length) return '';
    const firstSlide = (pitch.slides || []).find(s => !s.hidden);
    const bgId = firstSlide?.backgroundImage?.id;
    if (!bgId) return '';
    return backgrounds.find(b => b.id === bgId)?.url || '';
  })();

  // Load org users + CRM contacts once
  useEffect(() => {
    if (!open || contactsLoaded.current) return;
    setLoadingContacts(true);
    contactsLoaded.current = true;
    Promise.allSettled([
      getDocs(collection(db, 'users')),
      getDocs(query(collectionGroup(db, 'contacts'), where('email', '!=', ''))),
    ]).then(([usersResult, crmResult]) => {
      const orgUsers = usersResult.status === 'fulfilled'
        ? usersResult.value.docs
            .map(d => ({ id: `org_${d.id}`, ...d.data(), photoUrl: d.data().photoURL || '', source: 'org' }))
            .filter(u => u.email && u.name)
        : [];
      const crmContacts = crmResult.status === 'fulfilled'
        ? crmResult.value.docs
            .map(d => ({ id: `crm_${d.id}`, ...d.data(), source: 'crm' }))
            .filter(c => c.email && c.name)
        : [];
      const list = [...orgUsers, ...crmContacts]
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sv'));
      setAllContacts(list);
    }).finally(() => setLoadingContacts(false));
  }, [open]);

  // Reset state on open
  useEffect(() => {
    if (!open) return;
    setSubject(defaultSubject);
    setBodyText('');
    setRecipients([]);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleGenerate() {
    if (!pitch) return;
    setGenerating(true);
    const text = await generateSummary({ pitch });
    if (text) setBodyText(text);
    setGenerating(false);
  }

  async function handleSend() {
    if (!bodyText.trim() || !subject.trim() || !recipients.length) return;
    setSending(true);
    try {
      await authFetch('/.netlify/functions/pitch-mail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject,
          bodyText,
          heroImageUrl: heroImageUrl || '',
          shareUrl,
          pitchTitle: pitch?.title || '',
          recipients: recipients.map(c => ({ name: c.name, email: c.email })),
        }),
      });

      await addDoc(collection(db, 'pitchMails'), {
        pitchId:        pitch?.id || '',
        pitchTitle:     pitch?.title || '',
        subject,
        bodyText,
        heroImageUrl:   heroImageUrl || '',
        shareUrl,
        recipientCount: recipients.length,
        recipients:     recipients.map(c => ({ name: c.name, email: c.email })),
        sentBy:         currentUser?.uid || '',
        sentAt:         new Date(),
      });

      onClose('sent');
    } catch (e) {
      console.error('Kunde inte skicka:', e);
    } finally {
      setSending(false);
    }
  }

  if (!pitch) return null;

  return (
    <Dialog open={open} onClose={() => onClose(null)} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '90vh' } }}>
      <DialogTitle sx={{ bgcolor: '#1565C0', color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
        <PresentToAllIcon sx={{ fontSize: 22 }} />
        <Typography fontWeight={900} sx={{ fontSize: '1.1rem', flex: 1 }}>Dela presentation via mail</Typography>
        <Chip
          label={`${recipients.length} mottagare`}
          size="small"
          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.72rem' }}
        />
        <IconButton size="small" onClick={() => onClose(null)} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
        {/* ── LEFT: Composer ─────────────────────────────────────────────────── */}
        <Box sx={{ width: '45%', flexShrink: 0, p: 2.5, overflowY: 'auto', borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Subject */}
          <TextField
            label="Ämnesrad"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            size="small" fullWidth
            InputProps={{ sx: { fontWeight: 700, fontSize: '0.85rem' } }}
          />

          {/* Summary with AI */}
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75, fontSize: '0.62rem' }}>
              Sammanfattning
            </Typography>
            <TextField
              multiline rows={4} fullWidth size="small"
              placeholder="Skriv en kort sammanfattning av presentationen som motiverar mottagaren att öppna den..."
              value={bodyText}
              onChange={e => setBodyText(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
            />
            <Button
              size="small" variant="contained" fullWidth
              startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <AutoFixHighIcon sx={{ fontSize: 16 }} />}
              disabled={generating}
              onClick={handleGenerate}
              sx={{ mt: 1, textTransform: 'none', bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
              {generating ? 'Genererar...' : 'Generera sammanfattning med AI'}
            </Button>
          </Box>

          {/* Hero image info */}
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5, fontSize: '0.62rem' }}>
              Herobild (förstaslidets bakgrund)
            </Typography>
            {heroImageUrl
              ? <Box component="img" src={heroImageUrl} alt=""
                  sx={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 1, display: 'block' }} />
              : <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>Ingen bakgrundsbild på förstabilden</Typography>
            }
          </Box>

          {/* Share URL */}
          <TextField
            label="Delningslänk"
            value={shareUrl}
            size="small" fullWidth
            InputProps={{ readOnly: true, sx: { fontSize: '0.78rem', fontFamily: 'monospace' } }}
          />

          {/* Recipient picker */}
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75, fontSize: '0.62rem' }}>
              Mottagare
            </Typography>
            <Autocomplete
              multiple
              options={allContacts}
              loading={loadingContacts}
              value={recipients}
              onChange={(_, val) => setRecipients(val)}
              getOptionLabel={c => `${c.name} <${c.email}>`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              groupBy={c => c.source === 'org' ? 'Organisation' : 'CRM-kontakter'}
              renderOption={(props, c) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                  <Avatar src={c.photoUrl || undefined}
                    sx={{ width: 28, height: 28, fontSize: '0.65rem', bgcolor: c.source === 'org' ? '#1565C0' : '#6D4C41' }}>
                    {(c.name || '?')[0].toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.2 }}>{c.name}</Typography>
                      {c.source === 'org' && (
                        <Chip label="Intern" size="small"
                          sx={{ height: 14, fontSize: '0.58rem', bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                      {c.email}{c.role ? ` · ${c.role}` : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderTags={(val, getTagProps) =>
                val.map((c, i) => (
                  <Chip
                    {...getTagProps({ index: i })}
                    key={c.id}
                    label={c.name}
                    avatar={<Avatar src={c.photoUrl || undefined} sx={{ fontSize: '0.55rem' }}>{(c.name || '?')[0]}</Avatar>}
                    size="small"
                    sx={{ fontSize: '0.72rem', bgcolor: c.source === 'org' ? '#E3F2FD' : undefined }}
                  />
                ))
              }
              renderInput={params => (
                <TextField {...params} size="small" placeholder="Sök namn eller e-post..."
                  InputProps={{ ...params.InputProps, sx: { fontSize: '0.85rem' } }} />
              )}
              noOptionsText={loadingContacts ? 'Laddar...' : 'Inga kontakter hittades'}
            />
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block', fontSize: '0.65rem' }}>
              Kollegor i organisationen och externa CRM-kontakter
            </Typography>
          </Box>
        </Box>

        {/* ── RIGHT: Preview ──────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, bgcolor: '#f5f5f5', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #eee', bgcolor: '#fff' }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.62rem' }}>
              Förhandsgranskning
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <EmailPreview
              subject={subject}
              bodyText={bodyText}
              heroImageUrl={heroImageUrl}
              shareUrl={shareUrl}
              pitchTitle={pitch.title}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #eee', gap: 1 }}>
        <Button onClick={() => onClose(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Avbryt
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained" size="large"
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          disabled={!bodyText.trim() || !subject.trim() || sending || recipients.length === 0}
          onClick={handleSend}
          sx={{ textTransform: 'none', bgcolor: '#1565C0', '&:hover': { bgcolor: '#0D47A1' }, fontWeight: 700 }}>
          {sending ? 'Skickar...' : `Skicka till ${recipients.length} mottagare`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
